import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const ACCOUNT_MENU_LABELS = [
  { pattern: /\/orders(?:\?|$)/, source: ['订单', 'Order'], label: 'Orders' },
  { pattern: /\/profile(?:\?|$)|\/addresses(?:\?|$)/, source: ['个人资料', '资料'], label: 'Profile' },
];

const ACCOUNT_DIALOG_STYLE_ID = 'phoenix-account-dialog-fix';
const ACCOUNT_DOCUMENT_STYLE_ID = 'phoenix-account-document-fix';
const ACCOUNT_OVERLAY_ID = 'phoenix-account-backdrop';

const injectAccountDocumentStyle = () => {
  if (document.getElementById(ACCOUNT_DOCUMENT_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = ACCOUNT_DOCUMENT_STYLE_ID;
  style.textContent = `
    /* Phoenix launch fix: prevent page scroll behind the native account panel. */
    html[phoenix-account-open],
    html[phoenix-account-open] body {
      overflow: hidden !important;
      overscroll-behavior: none !important;
    }

    #phoenix-account-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1900;
      background: rgba(17, 17, 17, 0.28);
      opacity: 1;
    }
  `;
  document.head.append(style);
};

const closeAccountPanel = (account) => {
  const root = account.shadowRoot;
  if (!root) return;

  let openElement;
  try {
    openElement = root.querySelector('dialog[open], [popover]:popover-open, .dialog[open]');
  } catch (_error) {
    openElement = root.querySelector('dialog[open], .dialog[open]');
  }
  if (openElement?.close) openElement.close();
  if (openElement?.hidePopover) openElement.hidePopover();
};

const setAccountOpenState = (account, isOpen) => {
  injectAccountDocumentStyle();
  document.documentElement.toggleAttribute('phoenix-account-open', isOpen);

  let overlay = document.getElementById(ACCOUNT_OVERLAY_ID);

  if (isOpen && !overlay) {
    overlay = document.createElement('div');
    overlay.id = ACCOUNT_OVERLAY_ID;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.addEventListener('click', () => closeAccountPanel(account));
    document.body.append(overlay);
  } else if (!isOpen && overlay) {
    overlay.remove();
  }
};

const isAccountPanelOpen = (root) => {
  try {
    return Boolean(root.querySelector('dialog[open], [popover]:popover-open, .dialog[open], [aria-modal="true"]'));
  } catch (_error) {
    return Boolean(root.querySelector('dialog[open], .dialog[open], [aria-modal="true"]'));
  }
};

const updateAccountLinkLabel = (link, label) => {
  link.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      node.remove();
      return;
    }

    if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.tagName.toLowerCase() !== 'svg' &&
      node.dataset.phoenixAccountLabel !== 'true'
    ) {
      node.remove();
    }
  });

  let labelElement = link.querySelector('[data-phoenix-account-label="true"]');

  if (!labelElement) {
    labelElement = document.createElement('span');
    labelElement.dataset.phoenixAccountLabel = 'true';
    link.append(labelElement);
  }

  labelElement.textContent = label;
};

const normalizeAccountMenu = (root) => {
  root.querySelectorAll('a').forEach((link) => {
    const href = link.getAttribute('href') || '';
    const text = link.textContent.trim();
    const match = ACCOUNT_MENU_LABELS.find((item) => item.pattern.test(href) || item.source.includes(text));

    if (match && text !== match.label) {
      updateAccountLinkLabel(link, match.label);
    }
  });
};

const injectAccountDialogStyle = (root) => {
  if (root.getElementById(ACCOUNT_DIALOG_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = ACCOUNT_DIALOG_STYLE_ID;
  style.textContent = `
    /* Phoenix launch fix: keep the native account UI opaque and above storefront content. */
    :host {
      position: relative !important;
      z-index: 1200 !important;
    }

    .dialog,
    dialog,
    [popover],
    [role="dialog"],
    .panel,
    .menu {
      position: fixed !important;
      z-index: 2000 !important;
      background: #fff !important;
      background-color: #fff !important;
      opacity: 1 !important;
      color: #111 !important;
      isolation: isolate !important;
      box-shadow: 0 24px 70px rgba(17, 17, 17, 0.24) !important;
    }

    .dialog *,
    dialog *,
    [popover] *,
    [role="dialog"] * {
      opacity: 1 !important;
    }

    .dialog::backdrop,
    dialog::backdrop,
    [popover]::backdrop {
      position: fixed !important;
      inset: 0 !important;
      z-index: 1990 !important;
      background: rgba(17, 17, 17, 0.28) !important;
      opacity: 1 !important;
      backdrop-filter: none !important;
    }

    @media (min-width: 751px) {
      .dialog {
        width: min(320px, calc(100vw - 48px)) !important;
        max-width: min(320px, calc(100vw - 48px)) !important;
        top: calc(var(--header-height, 72px) + 16px) !important;
        inset-inline-end: 32px !important;
      }
    }

    @media (max-width: 750px) {
      .dialog,
      dialog,
      [popover] {
        width: min(100vw - 24px, 420px) !important;
        max-width: calc(100vw - 24px) !important;
        top: calc(var(--header-height, 64px) + 12px) !important;
        inset-inline: 12px !important;
      }
    }
  `;
  root.append(style);
};

const connectAccountFix = (account) => {
  if (account.__phoenixAccountFixConnected) return;

  account.__phoenixAccountFixConnected = true;

  const sync = () => {
    const root = account.shadowRoot;
    if (!root) return;

    injectAccountDialogStyle(root);
    normalizeAccountMenu(root);
    setAccountOpenState(account, isAccountPanelOpen(root));

    if (!account.__phoenixAccountFixObserver) {
      account.__phoenixAccountFixObserver = new MutationObserver(() => {
        injectAccountDialogStyle(root);
        normalizeAccountMenu(root);
      });
      account.__phoenixAccountFixObserver.observe(root, { childList: true, characterData: true, subtree: true });
    }
  };

  const syncBurst = () => {
    [0, 100, 350, 900, 1800, 3000].forEach((delay) => {
      setTimeout(sync, delay);
    });
  };

  account.addEventListener('open', syncBurst);
  account.addEventListener('close', () => setAccountOpenState(account, false));
  syncBurst();
};

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#connectAccountDropdownFix();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };

  #connectAccountDropdownFix = () => {
    customElements.whenDefined('shopify-account').then(() => {
      this.querySelectorAll('shopify-account').forEach(connectAccountFix);
    });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
